import { Form, Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useCurrentUser } from '../../Session';
import { DeviceSettingsType, TemplateUseCases, useGetMessageTemplateSettingsQuery } from '../../api';
import { useFlutter } from '../../components/FlutterProvider';
import { useLoadingController } from '../../components/LoadingProvider';
import { SettingsHeader, SettingsBody } from './components';
import SettingsUpdates from './components/SettingsUpdates';

export type FormValues = {
    biometric: boolean;
    pushNotification: boolean;
    biddingOpenedTemplateUseCase: string;
    bidSubmissionTemplateUseCase: string;
    noChancesLeftTemplateUseCase: string;
    bidAwardedTemplateUseCase: string;
    otpTemplateUseCase: string;
    userAccountRejectionTemplateUseCase: string;
    userAccountApprovalTemplateUseCase: string;
};

const noSubmit = () => undefined;

const Settings = () => {
    const { deviceFingerPrint } = useFlutter();

    const { deviceSettings } = useCurrentUser();

    const { data: messageTemplateSettingsData, loading: messageTemplateSettingsLoading } =
        useGetMessageTemplateSettingsQuery();

    useLoadingController(messageTemplateSettingsLoading);

    const useCases = useMemo(() => messageTemplateSettingsData?.messageTemplateSettings, [messageTemplateSettingsData]);

    const getTemplateSid = useCallback(
        (useCase: TemplateUseCases) =>
            useCases?.find(setting => setting.useCase === useCase)?.selectedTemplateSid ?? '',
        [useCases]
    );

    const initialValues = useMemo((): FormValues => {
        const find = (type: DeviceSettingsType) =>
            deviceSettings.some(setting => setting.type === type && setting.deviceFingerprint === deviceFingerPrint);

        return {
            biometric: find(DeviceSettingsType.Biometric),
            pushNotification: find(DeviceSettingsType.Push),
            biddingOpenedTemplateUseCase: getTemplateSid(TemplateUseCases.BiddingOpened),
            bidSubmissionTemplateUseCase: getTemplateSid(TemplateUseCases.BidSubmission),
            noChancesLeftTemplateUseCase: getTemplateSid(TemplateUseCases.NoChancesLeft),
            bidAwardedTemplateUseCase: getTemplateSid(TemplateUseCases.BidAwarded),
            otpTemplateUseCase: getTemplateSid(TemplateUseCases.Otp),
            userAccountRejectionTemplateUseCase: getTemplateSid(TemplateUseCases.UserAccountRejection),
            userAccountApprovalTemplateUseCase: getTemplateSid(TemplateUseCases.UserAccountApproval),
        };
    }, [deviceFingerPrint, deviceSettings, getTemplateSid]);

    // Render Formik only after data exists
    // to prevent users from seeing the form values being populated
    // while initialValues are not set yet
    if (!useCases) {
        return null;
    }

    return (
        <Formik key={deviceFingerPrint || 'none'} initialValues={initialValues} onSubmit={noSubmit}>
            {() => (
                <Form>
                    <SettingsUpdates />
                    <SettingsHeader />
                    <SettingsBody />
                </Form>
            )}
        </Formik>
    );
};

export default Settings;
